/**
 * Data layer for `plan-pause-for-low-season-customers`. Thin typed fetch wrappers over /api/v1/plan-pause-for-low-season-customers.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface PlanPauseForLowSeasonCustomersRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListPlanPauseForLowSeasonCustomersResponse {
	count: number;
	items: PlanPauseForLowSeasonCustomersRow[];
}

export interface CreatePlanPauseForLowSeasonCustomersBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/plan-pause-for-low-season-customers";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListPlanPauseForLowSeasonCustomersResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPlanPauseForLowSeasonCustomersResponse>(res);
}

export async function create(body: CreatePlanPauseForLowSeasonCustomersBody): Promise<PlanPauseForLowSeasonCustomersRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<PlanPauseForLowSeasonCustomersRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const planPauseForLowSeasonCustomersQueryKeys = {
	all: ["plan-pause-for-low-season-customers"] as const,
	list: () => [...planPauseForLowSeasonCustomersQueryKeys.all] as const,
	detail: (id: string) => [...planPauseForLowSeasonCustomersQueryKeys.all, id] as const,
};
